import { getProxiedUrl } from '@/utils/proxy'

export function validateUrl(url: string): { valid: boolean; error: string } {
  if (!url) {
    return { valid: false, error: '请输入网站链接' }
  }
  try {
    new URL(url)
    return { valid: true, error: '' }
  } catch {
    return { valid: false, error: '请输入有效的网站链接，如 https://example.com' }
  }
}

export function extractMainDomain(url: string): string {
  if (!url) return ''
  try {
    const parsed = new URL(url.trim())
    const hostname = parsed.hostname.replace(/^www\./, '')
    return `${parsed.protocol}//${hostname}`
  } catch {
    return url
  }
}

export interface HaloSiteVerifyResult {
  isHalo: boolean
  version: string
  error: string
  title: string
  favicon: string
}

function extractTitle(doc: Document): string {
  const titleTag = doc.querySelector('title')
  if (titleTag?.textContent) {
    return titleTag.textContent.trim()
  }
  const ogTitle = doc.querySelector('meta[property="og:title"]')
  if (ogTitle) {
    return ogTitle.getAttribute('content')?.trim() || ''
  }
  return ''
}

function extractFavicon(doc: Document, baseUrl: string): string {
  const iconLink = doc.querySelector('link[rel="icon"]') || doc.querySelector('link[rel="shortcut icon"]')
  if (iconLink) {
    const href = iconLink.getAttribute('href')
    if (href) {
      try {
        return new URL(href, baseUrl).href
      } catch {
        return ''
      }
    }
  }
  try {
    return new URL('/favicon.ico', baseUrl).href
  } catch {
    return ''
  }
}

export async function verifyHaloSite(url: string): Promise<HaloSiteVerifyResult> {
  const emptyResult = { title: '', favicon: '' }
  const fetchUrl = getProxiedUrl(url)

  try {
    const response = await fetch(fetchUrl, {
      headers: {
        'Accept': 'text/html'
      }
    })

    if (!response.ok) {
      return {
        isHalo: false,
        version: '',
        error: `无法访问网站 (HTTP ${response.status})`,
        ...emptyResult
      }
    }

    const html = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const title = extractTitle(doc)
    const favicon = extractFavicon(doc, url)

    const generatorMeta = doc.querySelector('meta[name="generator"]')

    if (!generatorMeta) {
      return {
        isHalo: false,
        version: '',
        error: '该网站不是 Halo 搭建的站点',
        title,
        favicon
      }
    }

    const content = generatorMeta.getAttribute('content') || ''
    const haloMatch = content.match(/^Halo\s+(.+)$/)

    if (!haloMatch) {
      return {
        isHalo: false,
        version: '',
        error: '未检测到 Halo 生成器标识',
        title,
        favicon
      }
    }

    return {
      isHalo: true,
      version: haloMatch[1],
      error: '',
      title,
      favicon
    }
  } catch (e: unknown) {
    if (e instanceof TypeError && e.message.includes('Failed to fetch')) {
      return {
        isHalo: false,
        version: '',
        error: '无法访问该网站，请检查链接是否正确或网站是否允许跨域访问',
        ...emptyResult
      }
    }
    const errorMessage = e instanceof Error ? e.message : '未知错误'
    return {
      isHalo: false,
      version: '',
      error: `验证失败: ${errorMessage}`,
      ...emptyResult
    }
  }
}
